import { useMountEffect, useSyncedRef, useTimeoutEffect } from "@react-hookz/web";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSdkContext } from "~/components/contexts";
import { addDays, isAfterOrEqual, isValid, notEmpty, safeSetLocalStorage } from "~/lib/utils";
export const useRenderPopup = (popupGetters, renderPopupOptions) => {
  const {
    acquisition: {
      popupState
    },
    currentRouteWithoutQuerystring
  } = useSdkContext();
  const {
    setWithTTL
  } = usePopupLocalStorage();
  const [popupDelayElapsed, setPopupDelayElapsed] = useState(false);
  const [popup, setPopup] = useState();
  const isPopupSet = useRef(false);
  const popupReference = useSyncedRef(popup);
  // https://www.seroundtable.com/google-cumulative-layout-shift-metric-update-31233.html
  // delay for 5 seconds to show popups
  const globalPopupDelay = 5000; // ms

  useTimeoutEffect(() => {
    setPopupDelayElapsed(true);
  }, globalPopupDelay);
  const triggerPopup = useCallback(() => {
    const fromActionGetters = Object.values(popupGetters).flatMap(getter => {
      var _getter$getNextFromAc;
      return (_getter$getNextFromAc = getter.getNextFromAction) !== null && _getter$getNextFromAc !== void 0 ? _getter$getNextFromAc : [];
    });
    for (const getter of fromActionGetters) {
      const handleClose = () => {
        if (nextPopup !== null && nextPopup !== void 0 && nextPopup.localStorageKey && !nextPopup.updateLocalStorageOnShow) {
          setWithTTL(nextPopup.localStorageKey);
        }
        setPopup(undefined);
      };
      const nextPopup = getter(handleClose);
      if (nextPopup) {
        setPopup(nextPopup);
        if (nextPopup.updateLocalStorageOnShow) setWithTTL(nextPopup.localStorageKey);
        return;
      }
    }
    if (!popupDelayElapsed || renderPopupOptions.skipTriggerFromConfig) return;
    const fromConfigGetters = Object.values(popupGetters).map(getter => getter.getNextFromConfig);
    for (const getter of fromConfigGetters) {
      const handleClose = () => {
        if (nextPopup !== null && nextPopup !== void 0 && nextPopup.localStorageKey && !nextPopup.updateLocalStorageOnShow) {
          setWithTTL(nextPopup.localStorageKey);
        }
        isPopupSet.current = false;
        setPopup(undefined);
      };
      const nextPopup = getter(handleClose);
      if (nextPopup) {
        var _nextPopup$delay;
        isPopupSet.current = true;
        setTimeout(() => {
          setPopup(nextPopup);
          if (nextPopup.updateLocalStorageOnShow) setWithTTL(nextPopup.localStorageKey);
        }, Math.max(((_nextPopup$delay = nextPopup.delay) !== null && _nextPopup$delay !== void 0 ? _nextPopup$delay : 0) - globalPopupDelay, 0));
        return;
      }
    }
    const fromScrollPositionGetters = Object.values(popupGetters).flatMap(getter => {
      var _getter$getNextFromSc;
      return (_getter$getNextFromSc = getter.getNextFromScrollPosition) !== null && _getter$getNextFromSc !== void 0 ? _getter$getNextFromSc : [];
    });
    for (const getter of fromScrollPositionGetters) {
      const handleClose = () => {
        if (nextPopup !== null && nextPopup !== void 0 && nextPopup.localStorageKey && !nextPopup.updateLocalStorageOnShow) {
          setWithTTL(nextPopup.localStorageKey);
        }
        setPopup(undefined);
      };
      const nextPopup = getter(handleClose);
      if (nextPopup) {
        setPopup(nextPopup);
        if (nextPopup.updateLocalStorageOnShow) setWithTTL(nextPopup.localStorageKey);
        return;
      }
    }
  }, [popupDelayElapsed, popupGetters, setWithTTL, renderPopupOptions.skipTriggerFromConfig]);
  useEffect(() => {
    if (isPopupSet.current || !currentRouteWithoutQuerystring || popupReference.current !== undefined || !Object.values(popupState).some(state => notEmpty(state))) return;
    triggerPopup();
  }, [currentRouteWithoutQuerystring, popupReference, popupState, triggerPopup]);
  return {
    popup: popupReference.current
  };
};
const usePopupLocalStorage = () => {
  const setWithTTL = useCallback(popupKey => {
    safeSetLocalStorage(popupKey, addDays(new Date(), 90).toISOString());
  }, []);

  // Housekeeping effect
  useMountEffect(() => {
    for (const [localStorageKey] of Object.entries(localStorage).filter(([localStorageKey]) => ["ACQUISITION", "CAMPAIGN", "TOPIC_PACKAGE"].some(targetKey => localStorageKey.startsWith(targetKey)))) {
      var _localStorage$getItem, _localStorage$getItem2;
      // If value is not date (possibly is legacy value), reset it to be date value
      if (!isValid(new Date((_localStorage$getItem = localStorage.getItem(localStorageKey)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : ""))) {
        setWithTTL(localStorageKey);
        continue;
      }
      const itemIsExpired = isAfterOrEqual(new Date(), (_localStorage$getItem2 = localStorage.getItem(localStorageKey)) !== null && _localStorage$getItem2 !== void 0 ? _localStorage$getItem2 : "");
      itemIsExpired && localStorage.removeItem(localStorageKey);
    }
  });
  return {
    setWithTTL
  };
};